// ─────────────────────────────────────────────────────────────────────────────
// E-SIGN CONSENT — 15 U.S.C. § 7001(c) — SHOWN WHEN DELIVERY IS REFUSED.
//
// 🚨 THE RECITAL IS FETCHED, NEVER WRITTEN HERE. GET /borrower/esign-consent
// returns the exact wording the server will record. A copy in this file would
// be a second source of truth for a legal recital, and the two would drift the
// first time either was edited — leaving a consent record attesting to text the
// borrower never saw.
//
// ⚠️ MOUNTED ON A 451 FROM THE DOCUMENT ENDPOINT, not on a page-load check.
// The server decides; this renders its refusal. A client-side "do we have
// consent" check would be a second gate that can disagree with the real one.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function EsignConsentGate({ token, documentLabel, onConsented }) {
  const [recital, setRecital] = useState('');
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/borrower/esign-consent`, { headers });
        if (!cancelled) setRecital(r.data?.recital || '');
      } catch (_) {
        if (!cancelled) setError('Could not load the consent disclosure. Please refresh.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await axios.post(`${API_URL}/api/borrower/esign-consent`, { agreed: true }, { headers });
      onConsented?.();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not record your consent. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
      padding: 20, marginTop: 16,
    }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#111827' }}>
        Electronic Records Consent
      </h3>
      <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.7, margin: '0 0 14px' }}>
        Before we can show you {documentLabel} on screen, federal and Texas law require
        that you consent to receiving these records electronically. Please read the
        disclosure below and check the box if you agree.
      </p>
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
        padding: 16, fontSize: 13, color: '#374151', lineHeight: 1.65,
        maxHeight: 220, overflow: 'auto',
      }}>
        {recital || 'Loading…'}
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          disabled={!recital || saving}
          style={{ marginTop: 2 }}
        />
        <span style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>
          I have read and agree to the Electronic Records Consent above.
        </span>
      </label>
      {error && (
        <div style={{ marginTop: 12, fontSize: 13, color: '#b91c1c' }}>{error}</div>
      )}
      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={submit}
          disabled={!checked || saving || !recital}
          style={{
            padding: '10px 18px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 700,
            color: '#fff', background: (!checked || saving || !recital) ? '#9ca3af' : '#3a6ad9',
            cursor: (!checked || saving || !recital) ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Agree and continue'}
        </button>
        {/* ⚠️ THE PAPER ROUTE IS ON THE SCREEN, NOT ONLY IN THE RECITAL. A
            consent whose only alternative is invisible is not much of a choice. */}
        <span style={{ fontSize: 12.5, color: '#6b7280' }}>
          Prefer paper? Call 512-713-6016 or email support@godoorly.com.
        </span>
      </div>
    </div>
  );
}
